import { useDroppable } from '@dnd-kit/core';
import { isoDateInZone, partsInZone, type Task } from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { TaskChip } from './TaskChip.js';
import { dropTargetId } from './useCalendar.js';

/** Domingo primeiro, como no TickTick (§8ab) — ver `inicioDaSemanaNaGrade`. */
const CABECALHO = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.'];

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Quantos blocos cabem numa celula antes de virar "+N". */
const MAX_VISIVEIS = 4;

interface MonthViewProps {
  days: number[];
  byDay: Map<string, Task[]>;
  now: number;
  /** Mes em foco (1-12); os dias vizinhos aparecem esmaecidos. */
  focusMonth: number;
  onOpen: (task: Task) => void;
}

/**
 * Grade mensal de 6 semanas (spec 9.4).
 *
 * Sempre 42 celulas, mesmo quando o mes cabe em 5 semanas: uma grade que muda
 * de altura conforme o mes faz a interface pular a cada navegacao.
 *
 * Soltar num dia preserva a HORA da tarefa e troca so a data — arrastar de 14h
 * de terca para quarta deve manter as 14h, nao zerar para meia-noite.
 */
export function MonthView({ days, byDay, now, focusMonth, onOpen }: MonthViewProps) {
  const timeZone = useTimeZone();
  const hoje = isoDateInZone(timeZone, now);

  return (
    // Sem cartao em volta: no TickTick a grade e a propria pagina, separada so
    // por linhas finas. O fundo branco vem da superficie.
    <div className="-mx-3 flex min-h-0 flex-1 flex-col overflow-hidden bg-surface md:mx-0">
      <div className="grid grid-cols-7">
        {CABECALHO.map((dia) => (
          <div key={dia} className="py-1 text-center text-xs text-text-mut">
            {dia}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
        {days.map((day) => {
          const p = partsInZone(timeZone, day);
          const date = isoDateInZone(timeZone, day);

          return (
            <DayCell
              key={day}
              date={date}
              dayNumber={p.day}
              mes={p.month}
              foraDoMes={p.month !== focusMonth}
              ehHoje={date === hoje}
              tarefas={byDay.get(date) ?? []}
              now={now}
              onOpen={onOpen}
            />
          );
        })}
      </div>
    </div>
  );
}

interface DayCellProps {
  date: string;
  dayNumber: number;
  mes: number;
  foraDoMes: boolean;
  ehHoje: boolean;
  tarefas: Task[];
  now: number;
  onOpen: (task: Task) => void;
}

function DayCell({ date, dayNumber, mes, foraDoMes, ehHoje, tarefas, now, onOpen }: DayCellProps) {
  const { setNodeRef, isOver } = useDroppable({ id: dropTargetId({ kind: 'day', date }) });

  const visiveis = tarefas.slice(0, MAX_VISIVEIS);
  const restantes = tarefas.length - visiveis.length;

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex min-h-0 min-w-0 flex-col gap-[2px] overflow-hidden border-t border-l border-border px-0.5 pb-0.5 pt-1 transition-colors duration-150 first:border-l-0 [&:nth-child(7n+1)]:border-l-0',
        isOver ? 'bg-brand-blue-soft' : '',
      ].join(' ')}
    >
      <div className="flex h-5 shrink-0 items-center px-1">
        <span
          className={[
            'text-xs font-medium tabular-nums',
            ehHoje
              ? 'grid h-5 min-w-5 place-items-center rounded-full bg-brand-blue px-1 text-white'
              : '',
            foraDoMes && !ehHoje ? 'text-text-mut opacity-60' : '',
          ].join(' ')}
        >
          {/* O dia 1 leva o mes ("1 out"), como no TickTick: e o que marca a
              virada no meio da grade. */}
          {dayNumber === 1 ? `${dayNumber} ${MES_CURTO[mes - 1]}` : dayNumber}
        </span>
      </div>

      <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden">
        {visiveis.map((task) => (
          <TaskChip key={task.id} task={task} now={now} onOpen={onOpen} />
        ))}

        {restantes > 0 ? (
          <span className="px-1 text-[10px] text-text-mut">+{restantes}</span>
        ) : null}
      </div>
    </div>
  );
}
